<template>
  <div class="tariff-info">
    <template v-if="!isEdit">
      <div class="font-600 w-100">
        <template v-if="activeTariff?.remoteInteraction">
          <property-block>
            <template #text>
              <div class="paragraph-l mb-2">Квота удалённого взаимодействия</div>
              <v-img
                v-if="activeTariff.remoteInteraction.channelManagerCode"
                :src="
                  activeTariff.remoteInteraction.channelManagerCode === ChannelManagerCode.TravelLine
                    ? TLFullLogo
                    : activeTariff.remoteInteraction.channelManagerCode === ChannelManagerCode.Bnovo
                      ? BnovoFullLogo
                      : activeTariff.remoteInteraction.channelManagerCode === ChannelManagerCode.Kontur
                        ? KonturFullLogo
                        : ExelyFullLogo
                "
                width="auto"
                height="24"
              />
            </template>
            <template #content>
              <e-alert
                v-if="
                  (activeTariff.remoteInteraction.channelManagerCode === ChannelManagerCode.Bnovo ||
                    activeTariff.remoteInteraction.channelManagerCode === ChannelManagerCode.Kontur) &&
                  activeAccount?.status === ContractStatus.OnApproval
                "
                variant="warning"
                thin
              >
                <div class="paragraph-m_h mb-1 font-600">Дождитесь рассмотрения вашего аккаунта</div>
                <div class="paragraph-s_h text-secondary font-400">
                  После перехода аккаунта в статус активен вам будет доступен ID подключения. Используйте этот ID для
                  настройки подключения в личном кабинете Bnovo.
                </div>
              </e-alert>
              <template v-else>
                <div class="paragraph-xs text-ordinary mb-1">ID подключения</div>
                <div class="d-flex align-center">
                  <span>{ {{ activeTariff.remoteInteraction.interactionId }} }</span>
                  <copy-to-clipboard class="ml-2" :text="activeTariff.remoteInteraction.interactionId" />
                </div>
                <div
                  v-if="
                    activeTariff.remoteInteraction.channelManagerCode === ChannelManagerCode.Bnovo ||
                    activeTariff.remoteInteraction.channelManagerCode === ChannelManagerCode.Kontur
                  "
                  class="paragraph-m_h text-secondary font-400 mt-2"
                >
                  Если вы еще не создали канал продаж в личном кабинете
                  {{
                    activeTariff.remoteInteraction.channelManagerCode === ChannelManagerCode.Bnovo
                      ? 'Bnovo'
                      : 'Контур Отель'
                  }}, укажите ID подключения
                  <e-tooltip location="bottom" inline>
                    <template #activator>
                      <span class="link">при его добавлении</span>
                    </template>
                    <v-img
                      :src="
                        activeTariff.remoteInteraction.channelManagerCode === ChannelManagerCode.Kontur
                          ? KonturConnection
                          : BnovoConnection
                      "
                      width="640"
                    />
                  </e-tooltip>
                  <div class="mt-2">
                    Для актуализации настроек канала продаж
                    <e-tooltip location="bottom" inline>
                      <template #activator>
                        <span
                          v-if="activeTariff.remoteInteraction.channelManagerCode === ChannelManagerCode.Bnovo"
                          class="link"
                          >ищите его по ID подключения</span
                        >
                        <span v-else class="link">ищите его по наименованию «Алеан» в подключенных каналах</span>
                      </template>
                      <v-img
                        :src="
                          activeTariff.remoteInteraction.channelManagerCode === ChannelManagerCode.Kontur
                            ? KonturConnectionSearch
                            : BnovoConnectionSearch
                        "
                        width="660"
                      />
                    </e-tooltip>
                  </div>
                </div>
                <a
                  v-if="activeTariff.remoteInteraction.remoteIntegrationUrl"
                  :href="activeTariff.remoteInteraction.remoteIntegrationUrl"
                  target="_blank"
                  class="link d-inline-flex mt-4"
                >
                  В личный кабинет
                  {{
                    activeTariff.remoteInteraction.channelManagerCode === ChannelManagerCode.TravelLine
                      ? 'Travel Line'
                      : activeTariff.remoteInteraction.channelManagerCode === ChannelManagerCode.Bnovo
                        ? 'Bnovo'
                        : activeTariff.remoteInteraction.channelManagerCode === ChannelManagerCode.Kontur
                          ? 'Контур Отель'
                          : 'Exely'
                  }}
                  <e-icon name="arrowRight" class="ml-3" />
                </a>
              </template>
            </template>
          </property-block>
          <divider class="my-6" />
        </template>

        <property-block>
          <template #text>
            <div class="paragraph-l">Общая информация</div>
          </template>
          <template #content>
            <div class="input-placeholder text-secondary mb-1">Наименование Тарифного плана</div>
            <multilanguage-text v-if="activeTariff" :text="activeTariff.commonInformation.name">
              <template #default="{ value }">
                <div class="paragraph-m_h">{{ value }}</div>
              </template>
            </multilanguage-text>
            <skeleton-loader v-else width="150px" height="25px" />
            <div class="input-placeholder text-ordinary mb-1 mt-4">НДС</div>
            <div v-if="activeTariff" class="paragraph-m_h">{{ activeTariff.commonInformation.taxScheme.name }}</div>
            <skeleton-loader v-else width="150px" type="paragraph-m_h" />
          </template>
        </property-block>

        <divider class="my-6" />

        <property-block>
          <template #text>
            <div class="paragraph-l">Правила применения</div>
          </template>

          <template #content>
            <div class="input-placeholder text-ordinary mb-1">Правило штрафования</div>
            <div class="paragraph-m_h mb-4">
              <skeleton-loader v-if="!activeTariff" width="150px" type="paragraph-m_h" />
              <template v-else>
                {{ activeTariff.commonInformation.applyingRules.penaltyScheme.name }}
              </template>
            </div>
            <div class="input-placeholder text-ordinary mb-1">Оплата поставщику</div>
            <div class="paragraph-m_h mb-6">
              <skeleton-loader v-if="!activeTariff" width="150px" type="paragraph-m_h" />
              <template v-else>
                {{ activeTariff.commonInformation.applyingRules.supplierPayRule.name }}
              </template>
            </div>
          </template>
        </property-block>

        <property-block>
          <template #text>
            <div class="paragraph-l mb-2">Релиз-период</div>
            <div class="paragraph-s text-secondary font-400">
              Укажите период закрытия продаж по данному тарифу до расчетного часа заезда. Например, если заезд в 12:00 и
              вы указали 4 часа, то продажи закроются в 8:00.
            </div>
          </template>
          <template #content>
            <div class="input-placeholder text-ordinary mb-1">Закрывать продажи за</div>
            <div class="paragraph-m_h">
              <skeleton-loader v-if="!activeTariff" width="80px" type="paragraph-m_h" />
              <template v-else>
                {{ stopSalePeriodHours }}
                {{ declensionString(stopSalePeriodHours, ['час', 'часа', 'часов']) }}
              </template>
            </div>
          </template>
        </property-block>

        <property-block class="mt-6 mb-6">
          <template #text>
            <div class="paragraph-l">Расчётные часы</div>
          </template>
          <template #content>
            <div class="d-flex">
              <div class="mr-10">
                <div class="input-placeholder text-ordinary mb-1">Расчетный час заезда</div>
                <skeleton-loader v-if="!activeTariff" width="100px" type="paragraph-m_h" />
                <div v-else class="paragraph-m_h d-flex align-center">
                  {{ getTimeFromPTH(activeTariff.commonInformation.checkInOutSettings.checkInTime) }}
                  <e-icon name="clock" :size="20" class="ml-2 text-secondary" />
                </div>
              </div>
              <div>
                <div class="input-placeholder text-ordinary mb-1">Расчетный час выезда</div>
                <skeleton-loader v-if="!activeTariff" width="100px" type="paragraph-m_h" />
                <div v-else class="paragraph-m_h d-flex align-center">
                  {{ getTimeFromPTH(activeTariff.commonInformation.checkInOutSettings.checkOutTime) }}
                  <e-icon name="clock" :size="20" class="ml-2 text-secondary" />
                </div>
              </div>
            </div>
          </template>
        </property-block>

        <property-block
          v-if="activeTariff?.commonInformation.checkInOutSettings.nextDayCheckout"
          class="mt-4 mb-6 mb-md-2 mb-lg-0"
        >
          <template #content>
            <div class="d-flex align-center">
              <e-icon name="check" :size="20" class="text-blue mr-2" />
              Выезд на следующие сутки
              <help-tooltip class="ml-2">
                Когда установлена галочка "Выезд на следующие сутки", выезд происходит на следующие сутки. При снятой
                галочке заезд и выезд происходят в одни сутки.
              </help-tooltip>
            </div>
          </template>
        </property-block>
      </div>

      <operation-button
        v-if="!activeTariff?.remoteInteraction"
        variant="primary"
        class="ml-auto"
        prepend-icon-src="editPen"
        :operation="activeTariff?.operations.update"
        :loading="isEditTariffScheduleStartLoading"
        @click="handleEdit"
      >
        Редактировать
      </operation-button>
    </template>

    <template v-else>
      <div class="w-100">
        <tariff-form v-model="form" ref="tariffFormRef" />
      </div>

      <div class="tariff-info__edit-buttons">
        <e-btn variant="secondary" @click="handleCancel">Отмена</e-btn>
        <e-btn variant="primary" :loading="isEditTariffScheduleUpdateLoading" @click="handleSave">Сохранить</e-btn>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, reactive, ref } from 'vue';
import { onBeforeRouteLeave, useRoute } from 'vue-router';
import type { TariffType } from '@/parts/tariffs/store/tariffs';
import type { TariffFormComponent, TariffFormData } from '@/parts/tariffs/types/tariffs';
import BnovoFullLogo from '@/assets/img/pages/tariffs/channel-manager/bnovo-full.svg';
import ExelyFullLogo from '@/assets/img/pages/tariffs/channel-manager/exely-full.svg';
import KonturFullLogo from '@/assets/img/pages/tariffs/channel-manager/kontur-full.svg';
import TLFullLogo from '@/assets/img/pages/tariffs/channel-manager/travel_line-full.svg';
import BnovoConnectionSearch from '@/assets/img/pages/tariffs/channel-manager/tutorual-bnovo-search.webp';
import BnovoConnection from '@/assets/img/pages/tariffs/channel-manager/tutorual-bnovo.webp';
import KonturConnectionSearch from '@/assets/img/pages/tariffs/channel-manager/tutorual-kontur-search.webp';
import KonturConnection from '@/assets/img/pages/tariffs/channel-manager/tutorual-kontur.webp';
import CopyToClipboard from '@/core/components/buttons/CopyToClipboard.vue';
import OperationButton from '@/core/components/buttons/OperationButton.vue';
import Divider from '@/core/components/helpers/Divider.vue';
import PropertyBlock from '@/core/components/interface/PropertyBlock.vue';
import SkeletonLoader from '@/core/components/interface/SkeletonLoader.vue';
import { EAlert, EBtn, EIcon, ETooltip, HelpTooltip, MultilanguageText } from '@/core/components/ui';
import useConfirmationDialog from '@/core/store/useConfirmationDialog';
import { getTimeFromPTH } from '@/core/utils/date';
import { deepCloneObject } from '@/core/utils/object';
import { declensionString } from '@/core/utils/string';
import { ChannelManagerCode, ContractStatus } from '@/graphql/generated';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';
import TariffForm from '@/parts/tariffs/components/TariffForm.vue';
import { useTariffsStore } from '@/parts/tariffs/store/tariffs';

const emit = defineEmits<{
  (e: 'checkInOutChanged'): void;
}>();

const $route = useRoute();

const { activeAccount } = useAccommodationObjectsStore();
const {
  activeTariff,
  setActiveTariff,
  editTariffScheduleStart,
  isEditTariffScheduleStartLoading,
  editTariffScheduleCancel,
  editTariffScheduleUpdate,
  isEditTariffScheduleUpdateLoading,
} = useTariffsStore();
const { showConfirmationDialog } = useConfirmationDialog();

const tariffFormRef = ref<TariffFormComponent>();
const isEdit = ref(false);

const form = reactive<TariffFormData>({
  name: null,
  nds: null,
  penaltyScheme: null,
  supplierPayRules: null,
  stopSalePeriod: null,
  time: ['', ''],
  checkOutOnNextDay: false,
});

const stopSalePeriodHours = computed(
  () => activeTariff.value?.commonInformation.applyingRules.stopSalePeriodHours ?? 0,
);

onBeforeUnmount(async () => {
  if (isEdit.value) {
    await editTariffScheduleCancel();
    isEdit.value = false;
  }
});

onBeforeRouteLeave((to, from, next) => {
  if (isEdit.value) {
    showConfirmationDialog({ action: next });
  } else {
    next();
  }
});

const handleEdit = async () => {
  if (!activeTariff.value) return;

  const result = await editTariffScheduleStart(String($route.params.t_id));

  const commonInformation = result?.data?.editTariffSchedule.TariffSchedule;

  if (commonInformation) {
    setActiveTariff({ ...activeTariff.value, commonInformation });
    presetForm(activeTariff.value);
    isEdit.value = true;
  }
};

const handleCancel = async () => {
  await editTariffScheduleCancel();
  isEdit.value = false;
};

const handleSave = async () => {
  const payload = tariffFormRef.value?.submit();

  if (!payload || !activeTariff.value) {
    return;
  }

  const result = await editTariffScheduleUpdate({ tariffScheduleCid: String($route.params.t_id), ...payload });

  const commonInformation = result?.data?.updateTariffSchedule.TariffSchedule;

  if (!result?.errors && commonInformation) {
    if (commonInformation.hasServiceSequenceChanged) {
      emit('checkInOutChanged');
    }

    setActiveTariff({
      ...activeTariff.value,
      commonInformation: {
        ...commonInformation,
        __typename: 'TariffScheduleCommonInformation' as const, // Overwrites and matches the target literal
      },
    });
    isEdit.value = false;
  }
};

const presetForm = (data: TariffType) => {
  if (data) {
    form.name = deepCloneObject(data.commonInformation.name);
    form.nds = data.commonInformation.taxScheme.cid;
    form.penaltyScheme = data.commonInformation.applyingRules.penaltyScheme.shortName;
    form.supplierPayRules = data.commonInformation.applyingRules.supplierPayRule.shortName;
    form.stopSalePeriod = data.commonInformation.applyingRules.stopSalePeriodHours;
    form.time = [
      data.commonInformation.checkInOutSettings.checkInTime,
      data.commonInformation.checkInOutSettings.checkOutTime,
    ];
    form.checkOutOnNextDay = data.commonInformation.checkInOutSettings.nextDayCheckout;
  }
};
</script>

<style scoped lang="scss">
.tariff-info {
  display: flex;
  justify-content: space-between;

  @include md-and-down {
    flex-direction: column;
  }

  &__edit-buttons {
    display: flex;
    justify-content: flex-end;

    gap: $gap-3;

    @include lg-and-up {
      margin-left: $gap-3;
    }

    @include md-and-down {
      margin-top: 24px;
    }

    @include sm-and-down {
      flex-direction: column-reverse;
    }

    @include xs {
      position: sticky;
      bottom: 0;
      z-index: 1;

      flex-direction: row;

      margin-top: 12px;
      margin-left: 0;
      padding: 12px 0;

      background-color: $main-bg-color;
    }
  }
}
</style>
